import {
  ChangeDetectionStrategy,
  Component,
  computed,
  inject,
  input,
  output,
  signal,
} from '@angular/core';
import { takeUntilDestroyed, toObservable } from '@angular/core/rxjs-interop';
import { MatButtonModule } from '@angular/material/button';
import { MatIconModule } from '@angular/material/icon';
import { MatSnackBar } from '@angular/material/snack-bar';
import { OrganisationService } from '@principle-theorem/ng-principle-shared';
import {
  APP_VERSION,
  NgCommonModule,
  TypedFormGroup,
} from '@principle-theorem/ng-shared';
import { AccClaimFundingRequest } from '@principle-theorem/principle-core';
import {
  ACC_PATIENT_FORM_STEP_MAP,
  AccPatientCommunicationType,
  AccPatientFormType,
  IAccClaimFormMetadata,
  IAccClaimFundingRequest,
  IAccClaimFundingRequestData,
  IAccPatient,
  IAccPatientFormTracking,
  IPatient,
  IStaffer,
} from '@principle-theorem/principle-core/interfaces';
import {
  filterUndefined,
  snapshotDefined,
  toTimestamp,
  WithRef,
} from '@principle-theorem/shared';
import { compact, isEmpty, isEqual, omit } from 'lodash';
import { combineLatest } from 'rxjs';
import { filter } from 'rxjs/operators';
import {
  AccClaimBuilder,
  IAccLodgementFormData,
} from '../../../acc/acc-claim-builder';
import { AccClaimService } from '../../../acc/acc-claim.service';
import { AccPatientFormsNotificationService } from '../../../acc/acc-patient-forms-notificaitons.service';
import { AccDentalDiagnosisFormComponent } from './form-components/acc-dental-diagnosis-form/acc-dental-diagnosis-form.component';
import { AccInjuryFormComponent } from './form-components/acc-injury-form/acc-injury-form.component';
import { AccPatientFormComponent } from './form-components/acc-patient-form/acc-patient-form.component';
import {
  AccPatientReferenceFormComponent,
  IAccSelectPatientForms,
  ISendAccNotificationEvent,
} from './form-components/acc-patient-reference-form/acc-patient-reference-form.component';
import { AccSummaryDeclarationFormComponent } from './form-components/acc-summary-declaration-form/acc-summary-declaration-form.component';
import { AccDiagnosisDetailsFormGroup } from './form-groups/acc-dental-diagnosis.formgroup';
import { AccEmploymentFormGroup } from './form-groups/acc-employment.formgroup';
import {
  AccAssistanceFormGroup,
  AccInjuryFormGroup,
} from './form-groups/acc-injury.formgroup';
import { AccPatientReferenceFormGroup } from './form-groups/acc-patient-referance.formgroup';
import { AccPatientFormGroup } from './form-groups/acc-patient.formgroup';
import { AccReferralManagementFormGroup } from './form-groups/acc-referrals.formgroup';
import { AccSummaryDeclarationFormGroup } from './form-groups/acc-summary-declaration.formgroup';
import { AccWorkCapacityFormGroup } from './form-groups/acc-work-capacity.formgroup';
import { AccAssistanceStepComponent } from './form-steps/acc-assistance-step/acc-assistance-step.component';
import { AccEmploymentStepComponent } from './form-steps/acc-employment-step/acc-employment-step.component';

interface IFormStep {
  title: string;
  forms: TypedFormGroup<object>[];
}

@Component({
  selector: 'pr-acc-claim-lodgement-form',
  imports: [
    NgCommonModule,
    MatButtonModule,
    MatIconModule,
    AccPatientFormComponent,
    AccInjuryFormComponent,
    AccDentalDiagnosisFormComponent,
    AccSummaryDeclarationFormComponent,
    AccEmploymentStepComponent,
    AccAssistanceStepComponent,
    AccPatientReferenceFormComponent,
  ],
  templateUrl: './acc-claim-lodgement-form.component.html',
  styleUrls: ['./acc-claim-lodgement-form.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
  providers: [AccPatientFormsNotificationService],
})
export class AccClaimLodgementFormComponent {
  private _organisation = inject(OrganisationService);
  private _appVersion = inject(APP_VERSION);
  private _notificationService = inject(AccPatientFormsNotificationService);
  private _accClaimService = inject(AccClaimService);
  private _snackBar = inject(MatSnackBar);
  private _stepValidityMap = signal<Record<number, boolean>>({});

  patient = input.required<WithRef<IPatient>>();
  testMode = input.required<boolean>();
  initialStep = input<number, AccPatientFormType>(0, {
    transform: (value) => ACC_PATIENT_FORM_STEP_MAP[value] ?? 0,
  });
  claim = input<WithRef<IAccClaimFundingRequest> | undefined>();
  savedClaim = signal<WithRef<IAccClaimFundingRequest> | undefined>(undefined, {
    equal: isEqual,
  });
  metadata = signal<IAccClaimFormMetadata | undefined>(undefined);
  claimSubmit = output<WithRef<IAccClaimFundingRequest>>();
  closeRequested = output<void>();
  // Form groups
  patientReferenceForm = new AccPatientReferenceFormGroup();
  patientForm = new AccPatientFormGroup();
  injuryForm = new AccInjuryFormGroup();
  employmentForm = new AccEmploymentFormGroup();
  workCapacityForm = new AccWorkCapacityFormGroup();
  dentalDiagnosisForm = new AccDiagnosisDetailsFormGroup();
  assistanceForm = new AccAssistanceFormGroup();
  referralForm = new AccReferralManagementFormGroup();
  summaryDeclarationForm = new AccSummaryDeclarationFormGroup();

  readonly forms = [
    this.patientReferenceForm,
    this.patientForm,
    this.injuryForm,
    this.employmentForm,
    this.workCapacityForm,
    this.dentalDiagnosisForm,
    this.assistanceForm,
    this.referralForm,
    this.summaryDeclarationForm,
  ];

  activeStepIndex = signal(0);
  isSubmitting = signal(false);
  disabled = signal(false);
  sendingForms = signal(false);

  canSubmit = computed(() => {
    const validityMap = this._stepValidityMap();
    return this.steps.every((_, index) => validityMap[index] === true);
  });

  currentStepValid = computed(() => {
    const validityMap = this._stepValidityMap();
    return validityMap[this.activeStepIndex()] === true;
  });

  submitButtonText = computed(() => {
    return this.isSubmitting() ? 'Submitting...' : 'Submit';
  });

  saveDraftButtonDisabled = computed(() => {
    return this.isSubmitting() || this.disabled() || this.testMode();
  });

  submitButtonDisabled = computed(() => {
    return !this.canSubmit() || this.saveDraftButtonDisabled();
  });

  nextButtonDisabled = computed(() => {
    return !this.currentStepValid();
  });

  stepValidities = computed(() => {
    const validityMap = this._stepValidityMap();
    return this.steps.map((_, index) => validityMap[index] === true);
  });

  hasUnsavedChanges = computed(() => {
    return this.forms.some((form) => form.dirty);
  });

  steps: IFormStep[] = [
    {
      title: 'Patient Reference',
      forms: [this.patientReferenceForm],
    },
    {
      title: 'Patient Details',
      forms: [this.patientForm],
    },
    {
      title: 'Accident Details',
      forms: [this.injuryForm],
    },
    {
      title: 'Employment Details',
      forms: [this.employmentForm, this.workCapacityForm],
    },
    {
      title: 'Diagnosis Details',
      forms: [this.dentalDiagnosisForm],
    },
    {
      title: 'Other Assistance',
      forms: [this.referralForm, this.assistanceForm],
    },
    {
      title: 'Summary and Declaration',
      forms: [this.summaryDeclarationForm],
    },
  ];

  constructor() {
    toObservable(this.claim)
      .pipe(filterUndefined(), takeUntilDestroyed())
      .subscribe((claim) => this.savedClaim.set(claim));

    combineLatest([toObservable(this.savedClaim), toObservable(this.patient)])
      .pipe(takeUntilDestroyed())
      .subscribe(([claim, patient]) => {
        const patientData = claim?.data.claimData.patient;

        if (!this._hasPatientData(patientData)) {
          void this.patientForm.patchPatientData(patient);
        }
      });

    toObservable(this.savedClaim)
      .pipe(filterUndefined(), takeUntilDestroyed())
      .subscribe((claim) => this._patchClaimData(claim));

    toObservable(this.savedClaim)
      .pipe(
        filterUndefined(),
        filter((claim) => !AccClaimFundingRequest.isClaimEditable(claim)),
        takeUntilDestroyed()
      )
      .subscribe((_claim) => {
        this.forms.forEach((form) => form.disable());
        this.disabled.set(true);
      });

    toObservable(this.initialStep)
      .pipe(takeUntilDestroyed())
      .subscribe((initialStep) => {
        this.setActiveStep(initialStep);
      });

    this._setupFormValidityTracking();
  }

  setActiveStep(index: number): void {
    if (index >= 0 && index < this.steps.length) {
      this.activeStepIndex.set(index);
    }
  }

  async next(): Promise<void> {
    this.setActiveStep(this.activeStepIndex() + 1);
    await this.saveProgress();
  }

  previous(): void {
    if (this.activeStepIndex() > 0) {
      this.setActiveStep(this.activeStepIndex() - 1);
    }
  }

  async submitForm(): Promise<void> {
    if (!this.canSubmit()) {
      return;
    }
    this.isSubmitting.set(true);
    const data = await this.transformFormDataToClaimFormat();
    const savedClaim = this.savedClaim();
    if (!data || !savedClaim) {
      return;
    }

    const claim: WithRef<IAccClaimFundingRequest> = {
      ...savedClaim,
      data,
    };
    this.claimSubmit.emit(claim);
    this.isSubmitting.set(false);
  }

  async saveProgress(): Promise<void> {
    const claim = this.savedClaim();
    const patient = this.patient();
    const disabled = this.disabled();
    const testMode = this.testMode();

    if (disabled || testMode) {
      return;
    }

    await this._saveClaim(patient, claim);
    this._snackBar.open('Progress saved');
    this.forms.forEach((form) => form.markAsPristine());
  }

  requestClose(): void {
    this.closeRequested.emit();
  }

  async transformFormDataToClaimFormat(
    metadataOverrides?: Partial<IAccClaimFormMetadata>
  ): Promise<IAccClaimFundingRequestData> {
    const practice = await snapshotDefined(this._organisation.practice$);
    const data = this._extractFormData();

    return {
      claimData: AccClaimBuilder.buildClaimData(
        this._appVersion,
        practice,
        data
      ),
      metadata: AccClaimBuilder.buildMetadata(
        data.employment,
        data.referrals,
        metadataOverrides
      ),
    };
  }

  async sendPatientForms(data: ISendAccNotificationEvent): Promise<void> {
    try {
      const patient = this.patient();
      const staffer = await snapshotDefined(this._organisation.staffer$);
      const practice = await snapshotDefined(this._organisation.practice$);
      const claim = await this._getClaim(patient);

      this.sendingForms.set(true);
      if (data.notificationType === AccPatientCommunicationType.SMS) {
        await this._notificationService.sendAsSMS(practice, patient, staffer);
      }
      if (data.notificationType === AccPatientCommunicationType.Email) {
        await this._notificationService.sendAsEmail(practice, patient);
      }

      await this._issueAccPatientForms(
        patient,
        claim,
        staffer,
        data.selectedForms
      );

      const patientForms = this._getPatientFormsMetadata(
        data.selectedForms,
        claim.data.metadata?.patientForms
      );

      await this._saveClaim(patient, claim, { patientForms });
      this.metadata.update((metadata) => ({ ...metadata, patientForms }));
    } catch (error) {
      // eslint-disable-next-line no-console
      console.error('Error sending ACC forms email', error);
    } finally {
      this.sendingForms.set(false);
    }
  }

  private async _issueAccPatientForms(
    patient: WithRef<IPatient>,
    claim: WithRef<IAccClaimFundingRequest>,
    staffer: WithRef<IStaffer>,
    selectedForms: IAccSelectPatientForms
  ): Promise<void> {
    const formsToIssue = (
      Object.entries(selectedForms) as [AccPatientFormType, boolean][]
    )
      .filter(([, selected]) => selected)
      .map(([formType]) => formType);

    if (formsToIssue.length === 0) {
      return;
    }

    await this._accClaimService.issuePatientForms(
      claim,
      patient,
      staffer,
      formsToIssue
    );
  }

  private _getPatientFormsMetadata(
    selectedForms: IAccSelectPatientForms,
    existingForms?: Record<AccPatientFormType, IAccPatientFormTracking>
  ): Record<AccPatientFormType, IAccPatientFormTracking> {
    return (
      Object.entries(selectedForms) as [AccPatientFormType, boolean][]
    ).reduce(
      (metadata, [formType, selected]) => ({
        ...metadata,
        [formType]: {
          selected,
          issuedAt: selected
            ? toTimestamp()
            : existingForms?.[formType]?.issuedAt,
        },
      }),
      {} as Record<AccPatientFormType, IAccPatientFormTracking>
    );
  }

  private async _getClaim(
    patient: WithRef<IPatient>
  ): Promise<WithRef<IAccClaimFundingRequest>> {
    const claim = this.savedClaim();
    return claim ? claim : this._saveClaim(patient);
  }

  private async _saveClaim(
    patient: WithRef<IPatient>,
    claim?: WithRef<IAccClaimFundingRequest>,
    additionalMetadata?: Partial<IAccClaimFormMetadata>
  ): Promise<WithRef<IAccClaimFundingRequest>> {
    const data = await this.transformFormDataToClaimFormat(additionalMetadata);
    const savedClaim = await this._accClaimService.saveClaim(
      patient,
      data,
      claim
    );
    this.savedClaim.set(savedClaim);
    return savedClaim;
  }

  private _setupFormValidityTracking(): void {
    this.forms.forEach((form) => {
      form.statusChanges.pipe(takeUntilDestroyed()).subscribe(() => {
        this._updateStepValidityMap();
      });
    });

    this._updateStepValidityMap();
  }

  private _extractFormData(): IAccLodgementFormData {
    return {
      patientReference: this.patientReferenceForm.value,
      patient: this.patientForm.value,
      injury: this.injuryForm.value,
      assistance: this.assistanceForm.value,
      employment: this.employmentForm.value,
      dentalDiagnosis: this.dentalDiagnosisForm.value,
      workCapacity: this.workCapacityForm.value,
      referrals: this.referralForm.value,
      declaration: this.summaryDeclarationForm.value,
    };
  }

  private _updateStepValidityMap(): void {
    const validityMap: Record<number, boolean> = {};
    this.steps.forEach((step, index) => {
      if (step.title === 'Employment Details') {
        const isPaid = this.employmentForm.isInPaidEmployment;
        validityMap[index] =
          this.employmentForm.valid && (!isPaid || this.workCapacityForm.valid);
      } else {
        validityMap[index] = step.forms.every((form) => form.valid);
      }
    });
    this._stepValidityMap.set(validityMap);
  }

  private _patchClaimData(claim: WithRef<IAccClaimFundingRequest>): void {
    const data = claim.data.claimData;
    const metadata = claim.data.metadata;

    this.metadata.set(metadata);

    if (data.patient) {
      this.patientForm.patchValue(data.patient);
    }
    if (data.injury) {
      this.injuryForm.patchValue(data.injury);
      this.assistanceForm.patchValue(data.injury);
    }

    if (data.workCapacity) {
      this.workCapacityForm.patch(data.workCapacity);
    }

    if (data.dentalDiagnosis) {
      this.dentalDiagnosisForm.patch(data.dentalDiagnosis);
    }

    if (data.declaration) {
      this.summaryDeclarationForm.patchValue(data.declaration);
    }

    this.referralForm.patch({
      includeReferrals: metadata?.includeReferrals,
      referrals: data.referrals || [],
    });

    if (data.employment) {
      this.employmentForm.patchValue({
        ...data.employment,
        occupationCode: metadata?.occupationCode,
        workInjury: data.injury?.workInjury,
      });
    }

    this.patientReferenceForm.patchValue({
      claimNumber: data?.claimNumber,
      provider: data?.provider?.details,
    });
  }

  private _hasPatientData(data: IAccPatient | undefined): boolean {
    if (!data) {
      return false;
    }

    const { details, contact } = data;
    const address = omit(data.address, 'type');

    const hasDetails = details && !isEmpty(compact(Object.values(details)));
    const hasContact = contact && !isEmpty(compact(Object.values(contact)));
    const hasAddress = address && !isEmpty(compact(Object.values(address)));
    return !!(hasDetails || hasContact || hasAddress);
  }
}
